import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import i18n from '@i18next/i18n';
import { getQuerySearchParams } from '@src/slide/infrastructure/module/independent/url/param-operation';
import { LocalToOnlineMainActionService } from './action-service/local-to-online-main-action-service';
import { LocalToOnlineMinorActionService } from './action-service/local-to-online-minor-action-service';
import { MonitorStatus } from '@tencent/slide-sdk-model/src/manager/logger.interface';
import { openUrlForAll } from '@src/slide/infrastructure/module/independent/url/open-url';
import { SyncManager } from './sync-manager';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { getToggleStatusByApi, ToggleType } from '@src/slide/infrastructure/module/dependent/config/toggle-config';
import type { IDocInfoManager } from '../../interfaces/doc-info.interface';
import type { ILogger } from '../../interfaces/logger.interface';
import type { IPresentationStoreService } from '../../interfaces/presentation-service.interface';
import { IImportComponent } from '@tencent/docs-service-interface';
import appConfigInstance from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import { CustomConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';

enum RecordType {
    ByImport = 'byImport',
    ByCollaborate = 'byCollaborate',
}

export class OnlineSyncManager extends SyncManager {
    protected timeRecord: {
        createStart: number,
        createEnd: number,
        createCost: number,
        upload50: number,
        upload100: number,
        uploadCost: number,
        total: number,
        url: string;
        localPadId?: string;
        slideCount: number;
        modelCount: number,
        size: number,
        resource: {
            total: number,
            audioCount: number,
            videoCount: number,
            imageCount: number,
        }
    };

    public constructor(
        protected docInfoManager: IDocInfoManager,
        protected presentationStoreService: IPresentationStoreService,
        protected logger: ILogger,
    ) {
        super(docInfoManager, presentationStoreService, logger);
        this.init();
    }

    public async createDoc(): Promise<void> {
        this.resetTimeRecord();
        if (this.isReady && this.checkDataIsSupported()) {
            await this.createDocByWorkerService();
        } else {
            await this.createDocBySCComponent();
        }
    }

    public async syncDoc() {
        throw new Error('onlineSyncManager unsupported syncDoc');
    }

    public changeReadyState(state: boolean) {
        this.ready = state;
    }

    public handleCreateSuccess(url: string) {
        this.timeRecord.createEnd = Date.now();
        this.timeRecord.url = url;
        this.timeRecord.createCost = this.timeRecord.createEnd - this.timeRecord.createStart;
    }

    public handleUpload50Progress() {
        if (this.timeRecord.upload50) return;
        this.timeRecord.upload50 = Date.now();
    }

    public handleUpload100Progress() {
        if (this.timeRecord.upload100) return;
        this.handleUpload50Progress();
        this.timeRecord.upload100 = Date.now();
        this.timeRecord.uploadCost = this.timeRecord.upload100 - this.timeRecord.createEnd;
        this.timeRecord.total = this.timeRecord.upload100 - this.timeRecord.createStart;

        this.reportTimeRecord(RecordType.ByCollaborate);
    }

    protected async init() {
        const isToggleOpen = await getToggleStatusByApi(ToggleType.NEW_LOCAL_FILE_TRANSFER);
        // 开关无法区分渠道
        const isSupported = isToggleOpen && !!appConfigInstance.getConfig(CustomConfig.newLocalFileTransfer as any);
        if (!isSupported) return;
        this.actionService = ENV.IS_LOCAL_EDIT
            ? new LocalToOnlineMainActionService(this.logger, this.docInfoManager, this)
            : new LocalToOnlineMinorActionService(this.logger, this);
        super.init();
    }

    protected async createDocByWorkerService() {
        this.timeRecord.createStart = Date.now();
        this.logger.monitor?.reportStatus?.('LFT-click', MonitorStatus.SUCCESS, '', [this.docInfoManager.userId ?? '']);

        this.showTips({ message: i18n.t('转多人在线中，请稍候'), closable: true });
        const createConfig = await this.getCreateConfig();
        this.workerService.syncManager.createOnlineDoc(createConfig);
    }

    protected checkDataIsSupported() {
        return true;
    }

    /**
     * 通过DC组件创建文档
     * 参考实现：
     * src/feature/TitlebarService/components/convert-to-online/index.tsx
     */
    protected async createDocBySCComponent() {
        const { IMPORT } = await import('@src/js/common/ark-extensions/dc-components/import-file/component-config');
        const ArkLoader = await import('@src/js/common/ark-extensions').then(t => t.default);
        const importExtension = await ArkLoader.extensionService.getExtension<IImportComponent>(IMPORT.name);

        this.timeRecord.createStart = Date.now();
        importExtension.import({
            localFilePath: getQuerySearchParams('localFilePath'),
            properties: {
                guideCloudPreDialog: 103,
            },
            report: {
                ver7: 2,
                aidPosition: 'detail',
            },
            onTaskFinish: ({ currentTask }) => {
                this.timeRecord.createEnd = Date.now();
                this.timeRecord.url = currentTask.fileUrl;
                this.timeRecord.total = this.timeRecord.createEnd - this.timeRecord.createStart;
                this.timeRecord.localPadId = this.docInfoManager.globalPadId;

                this.reportTimeRecord(RecordType.ByImport);

                openUrlForAll({
                    url: currentTask.fileUrl,
                });
            },
        });
    }

    protected getSupportedImageExtension(url?: string) {
        const regex = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
        const match = url?.match(regex);

        // 如果匹配成功，返回扩展名（不带点）
        return match ? match[1] : null;
    }

    protected resetTimeRecord() {
        const { resourceMap } = this.presentationStoreService.getPresentation().getOwnedData();
        const audioCount = Object.keys(resourceMap.audios).length;
        const videoCount = Object.keys(resourceMap.videos).length;
        const imageCount = Object.keys(resourceMap.images).length;
        this.timeRecord = {
            createStart: 0,
            createEnd: 0,
            createCost: 0,
            upload50: 0,
            upload100: 0,
            uploadCost: 0,
            total: 0,
            url: '',
            localPadId: this.docInfoManager.globalPadId,
            slideCount: this.presentationStoreService.getPresentation().slideList.length,
            modelCount: [...(this.presentationStoreService.getPresentation() as any).store.models.models.keys()].length,
            resource: {
                total: audioCount + videoCount + imageCount,
                audioCount,
                videoCount,
                imageCount,
            },
            size: 0,
        };
        this.getFileSize().then(size => this.timeRecord.size = size);
    }

    protected reportTimeRecord(recordType: RecordType) {
        if (__IS_DEV__) {
            const storageItem = localStorage.getItem(recordType);
            if (storageItem) {
                const timeArr = JSON.parse(storageItem);
                timeArr.push(this.timeRecord);
                localStorage.setItem(recordType, JSON.stringify(timeArr));
            } else {
                localStorage.setItem(recordType, JSON.stringify([this.timeRecord]));
            }
            return;
        }
        if (recordType === RecordType.ByCollaborate) {
            this.logger.tdw?.({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.LOCAL_DOC,
                action: TDWActionType.LOCAL_TO_ONLINE_BY_COLLABORATE,
                ver6: JSON.stringify(this.timeRecord),
            });
        } else {
            this.logger.tdw?.({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.LOCAL_DOC,
                action: TDWActionType.LOCAL_TO_ONLINE_BY_IMPORT,
                ver6: JSON.stringify(this.timeRecord),
            });
        }
        return;
    }

    protected async getFileSize() {
        return new Promise<number>((resolve) => {
            window.mqq.invoke('docx', 'getFileStats', {
                file: getQuerySearchParams('localFilePath'),
            }, (response) => {
                resolve(response.size as number);
            });
        });
    }
}
